import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { queryBucket } from './OrderQueries';
import { QUEUES, queueCount } from './intlOrderUtils';
import TrendChart from './TrendChart';
import DateRangePicker from './DateRangePicker';
import {
  PhoneIcon,
  CheckIcon,
  CloseIcon,
  RefreshIcon,
  HashIcon,
  UsersIcon,
  CalendarIcon,
  CalendarClockIcon,
  FlagIcon,
  CartIcon,
  WhatsAppIcon,
  MailIcon,
  GlobeIcon,
  LinkIcon,
  WalletIcon,
  ShieldIcon,
  ChevronRightIcon,
} from './Icons';
import {
  getCallStatus,
  getCustomerNumber,
  filterCallsByDate,
  localDateStr,
  daysAgo,
  buildDailyPoints,
  buildAgentStats,
} from './callUtils';

const today = () => localDateStr(new Date());

// Same Today/7/14/30 preset shape MyDashboard.js already uses for its own
// date-range filter — kept identical so switching between the two pages
// doesn't relearn a different set of buttons.
const PRESETS = [
  { key: 'today', label: 'Today', days: 1 },
  { key: '7', label: '7 Days', days: 7 },
  { key: '14', label: '14 Days', days: 14 },
  { key: '30', label: '30 Days', days: 30 },
];

// Pure tool pages with no queue/backlog of their own — rendered as quick-
// action buttons rather than a stat tile with nothing real to count.
const TOOL_KEYS = ['livecalls', 'dialpad', 'trends', 'query', 'all'];

const shiftDateStr = (dateStr, days) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);
  return localDateStr(date);
};

const summarizeCalls = (callsArr) => {
  const acc = { answered: 0, missed: 0, attempted: 0 };
  for (const call of callsArr) {
    const status = getCallStatus(call);
    if (status === 'Answered') acc.answered += 1;
    else if (status === 'Missed') acc.missed += 1;
    else if (status === 'Attempted') acc.attempted += 1;
  }
  return { ...acc, total: acc.answered + acc.missed + acc.attempted };
};

function KpiCard({ icon: Icon, color, value, label, trend, onClick }) {
  return (
    <button type="button" className="overview-kpi-card" onClick={onClick}>
      <div className="overview-kpi-top">
        <span className={`overview-icon-badge ${color}`}>
          <Icon width={18} height={18} />
        </span>
        {trend !== null && trend !== undefined && (
          <span className={`overview-kpi-trend ${trend >= 0 ? 'up' : 'down'}`}>
            {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}%
          </span>
        )}
      </div>
      <div className="stat-value">{value === null || value === undefined ? '-' : value}</div>
      <div className="stat-label">{label}</div>
    </button>
  );
}

function Section({ title, action, visible, children }) {
  if (!visible) return null;
  return (
    <div className="chart-card overview-section">
      <div className="trend-chart-header">
        <h3 className="heatmap-title">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function SectionError({ message }) {
  return <p className="call-feedback call-feedback-error">{message}</p>;
}

function greetingForHour(hour) {
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function Overview({ calls, isSuperadmin, navItems, onNavigate }) {
  const { user, authFetch } = useAuth();
  const hasKey = useMemo(() => new Set(navItems.map((item) => item.key)), [navItems]);

  const [sections, setSections] = useState({});

  const setSection = (key, patch) => {
    setSections((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  useEffect(() => {
    let cancelled = false;

    const fetchJson = async (url) => {
      const response = await authFetch(url);
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body?.error || 'Failed to load');
      return body;
    };

    const load = async (key, run) => {
      setSection(key, { status: 'loading' });
      try {
        const data = await run();
        if (!cancelled) setSection(key, { status: 'ready', data, error: null });
      } catch (err) {
        if (!cancelled) setSection(key, { status: 'error', error: err.message || 'Failed to load' });
      }
    };

    const loadAll = () => {
      if (hasKey.has('followup')) {
        load('followup', async () => {
          const data = await fetchJson('/api/follow-ups');
          return Array.isArray(data) ? data : [];
        });
      }
      if (hasKey.has('dailyorders')) {
        load('dailyorders', async () => {
          const [ordersBody, counts] = await Promise.all([
            fetchJson('/api/ivr/erp/daily-orders'),
            fetchJson(`/api/ivr/erp/daily-orders/action-counts?date=${today()}`),
          ]);
          return {
            pending: Array.isArray(ordersBody?.orders) ? ordersBody.orders.length : 0,
            confirmed: counts?.confirmed || 0,
            cancelled: counts?.cancelled || 0,
          };
        });
      }
      if (hasKey.has('orderqueries')) {
        load('orderqueries', async () => {
          const body = await fetchJson('/api/ivr/erp/order-queries?limit=500');
          const results = Array.isArray(body?.results) ? body.results : [];
          const counts = { pending: 0, open: 0, approval_pending: 0, done: 0 };
          results.forEach((q) => {
            const bucket = queryBucket(q);
            if (counts[bucket] !== undefined) counts[bucket] += 1;
          });
          return { total: results.length, ...counts };
        });
      }
      if (hasKey.has('abandonedcarts')) {
        load('abandonedcarts', async () => {
          const [cartsBody, counts] = await Promise.all([
            fetchJson('/api/ivr/abandoned-carts'),
            fetchJson(`/api/ivr/abandoned-carts/action-counts?date=${today()}`),
          ]);
          return {
            active: Array.isArray(cartsBody?.carts) ? cartsBody.carts.length : 0,
            converted: counts?.converted || 0,
            notConverted: counts?.notConverted || 0,
          };
        });
      }
      if (hasKey.has('whatsapp')) {
        load('whatsapp', async () => {
          const data = await fetchJson('/api/whatsapp/conversations');
          const conversations = Array.isArray(data) ? data : [];
          return {
            total: conversations.length,
            unread: conversations.filter((c) => c.unread_count > 0).length,
          };
        });
      }
      if (hasKey.has('email')) {
        // Counts only (GET /api/email/summary), not every conversation.
        load('email', async () => {
          const data = await fetchJson('/api/email/summary');
          return {
            total: Number(data?.total) || 0,
            unread: Number(data?.unread) || 0,
            needsReply: Number(data?.needs_reply) || 0,
          };
        });
      }
      if (hasKey.has('international')) {
        load('international', async () => {
          // Workflow queues (server/intlOrders). The older manual log on
          // the page's "Legacy log" tab isn't counted here.
          // Same counts as the page's own queue cards.
          const body = await fetchJson('/api/international-orders/summary');
          const count = (key) => queueCount(QUEUES.find((queue) => queue.key === key), body);
          return {
            logistics: count('logistics'),
            payment: count('payment'),
            toPack: count('packing'),
            toDispatch: count('dispatch'),
          };
        });
      }
      if (hasKey.has('paymentlinks')) {
        load('paymentlinks', async () => {
          const body = await fetchJson('/api/ivr/payment-links');
          const links = Array.isArray(body?.links) ? body.links : [];
          const counts = { created: 0, paid: 0, partially_paid: 0, cancelled: 0, expired: 0 };
          links.forEach((link) => {
            if (counts[link.status] !== undefined) counts[link.status] += 1;
          });
          return { total: links.length, ...counts };
        });
      }
      if (hasKey.has('backendpayments')) {
        load('backendpayments', async () => {
          const body = await fetchJson('/api/ivr/backend-payments');
          const payments = Array.isArray(body?.payments) ? body.payments : [];
          const total = payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
          return { count: payments.length, total };
        });
      }
      if (hasKey.has('admins')) {
        load('admins', async () => {
          const [admins, roles] = await Promise.all([fetchJson('/api/admins'), fetchJson('/api/roles')]);
          return {
            admins: Array.isArray(admins) ? admins.length : 0,
            roles: Array.isArray(roles) ? roles.length : 0,
          };
        });
      }
    };

    loadAll();
    // Overview is a glance page, not a working queue — refresh far less often
    // than the 10s polls individual pages use, so leaving this tab open
    // doesn't multiply this page's ~9 endpoints' worth of background load.
    const interval = setInterval(loadAll, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [authFetch, hasKey]);

  const todayStr = today();
  const [fromDate, setFromDate] = useState(daysAgo(7));
  const [toDate, setToDate] = useState(todayStr);
  const activePreset = PRESETS.find((preset) => fromDate === daysAgo(preset.days) && toDate === todayStr)?.key;
  const applyPreset = (preset) => {
    setFromDate(daysAgo(preset.days));
    setToDate(todayStr);
  };

  const rangeCalls = useMemo(() => filterCallsByDate(calls, fromDate, toDate), [calls, fromDate, toDate]);
  const points = useMemo(() => buildDailyPoints(calls, fromDate, toDate), [calls, fromDate, toDate]);
  const callTotals = useMemo(() => summarizeCalls(rangeCalls), [rangeCalls]);
  const pressedInRange = useMemo(() => rangeCalls.filter((c) => c.digit_pressed).length, [rangeCalls]);
  const agentCount = useMemo(() => buildAgentStats(rangeCalls).length, [rangeCalls]);

  // Trend badge on the Total Calls tile — this range's volume vs the
  // immediately preceding period of the same length, both derived from the
  // same already-loaded `calls` prop (no extra fetch).
  const callTrend = useMemo(() => {
    if (!fromDate || !toDate) return null;
    const rangeDays = Math.round((new Date(toDate) - new Date(fromDate)) / 86400000) + 1;
    const prevTo = shiftDateStr(fromDate, -1);
    const prevFrom = shiftDateStr(prevTo, -(rangeDays - 1));
    const prevTotals = summarizeCalls(filterCallsByDate(calls, prevFrom, prevTo));
    if (prevTotals.total === 0) return null;
    return Math.round(((callTotals.total - prevTotals.total) / prevTotals.total) * 100);
  }, [calls, fromDate, toDate, callTotals.total]);

  // "Needs attention" is always today's real backlog, independent of
  // whatever range the picker above is set to.
  const todaysCalls = useMemo(() => filterCallsByDate(calls, todayStr, todayStr), [calls, todayStr]);
  const missedToday = useMemo(() => summarizeCalls(todaysCalls).missed, [todaysCalls]);

  const followUpCounts = useMemo(() => {
    const data = sections.followup?.data || [];
    const now = Date.now();
    const isOverdue = (f) => f.follow_up_date && new Date(f.follow_up_date).getTime() < now;
    const pending = data.filter((f) => f.follow_up_status !== 'done');
    return { pending: pending.length, overdue: pending.filter(isOverdue).length };
  }, [sections.followup]);

  const attentionItems = useMemo(() => {
    const candidates = [
      { key: 'missed', navKey: 'incoming', color: 'danger', label: 'Missed Calls Today', count: missedToday, visible: hasKey.has('incoming') },
      { key: 'overdue', navKey: 'followup', color: 'critical', label: 'Overdue Follow-Ups', count: followUpCounts.overdue, visible: hasKey.has('followup') },
      { key: 'dailyorders', navKey: 'dailyorders', color: 'warning', label: 'Daily Orders Pending', count: sections.dailyorders?.data?.pending, visible: hasKey.has('dailyorders') },
      { key: 'abandonedcarts', navKey: 'abandonedcarts', color: 'warning', label: 'Abandoned Carts', count: sections.abandonedcarts?.data?.active, visible: hasKey.has('abandonedcarts') },
      { key: 'orderqueries', navKey: 'orderqueries', color: 'primary', label: 'Pending Order Queries', count: sections.orderqueries?.data?.pending, visible: hasKey.has('orderqueries') },
      { key: 'whatsapp', navKey: 'whatsapp', color: 'success', label: 'Unread WhatsApp', count: sections.whatsapp?.data?.unread, visible: hasKey.has('whatsapp') },
      { key: 'email', navKey: 'email', color: 'primary', label: 'Unread Email', count: sections.email?.data?.unread, visible: hasKey.has('email') },
      { key: 'emailreply', navKey: 'email', color: 'warning', label: 'Emails Needing a Reply', count: sections.email?.data?.needsReply, visible: hasKey.has('email') },
    ];
    return candidates
      .filter((item) => item.visible && Number(item.count) > 0)
      .sort((a, b) => b.count - a.count);
  }, [missedToday, followUpCounts, sections, hasKey]);

  const recentCalls = useMemo(() => calls.slice(0, 6), [calls]);
  const toolItems = navItems.filter((item) => TOOL_KEYS.includes(item.key));

  const displayName = user?.agentName || user?.username || 'there';
  const greeting = greetingForHour(new Date().getHours());
  const dateHeader = new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="overview-page">
      <div className="overview-greeting">
        <div>
          <div className="overview-greeting-date">{dateHeader}</div>
          <h2 className="overview-greeting-title">{greeting}, {displayName}</h2>
          <p className="overview-greeting-subtitle">Here's what's happening across Rajarani Product today.</p>
        </div>
        <div className="agent-date-filter">
          <div className="customer-toggle" role="group" aria-label="Date range preset">
            {PRESETS.map((preset) => (
              <button
                key={preset.key}
                type="button"
                className={activePreset === preset.key ? 'active' : ''}
                onClick={() => applyPreset(preset)}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <DateRangePicker
            fromDate={fromDate}
            toDate={toDate}
            maxDate={todayStr}
            onApply={(from, to) => {
              setFromDate(from);
              setToDate(to);
            }}
          />
        </div>
      </div>

      {hasKey.has('incoming') && (
        <div className="overview-kpi-grid">
          <KpiCard icon={PhoneIcon} color="primary" value={callTotals.total} label="Total Calls" trend={callTrend} onClick={() => onNavigate('incoming')} />
          <KpiCard icon={CheckIcon} color="success" value={callTotals.answered} label="Answered" onClick={() => onNavigate('incoming')} />
          {hasKey.has('dailyorders') && (
            <KpiCard
              icon={CalendarIcon}
              color="warning"
              value={sections.dailyorders?.status === 'error' ? '!' : sections.dailyorders?.data?.pending}
              label="Daily Orders Pending"
              onClick={() => onNavigate('dailyorders')}
            />
          )}
          {hasKey.has('followup') && (
            <KpiCard
              icon={CalendarClockIcon}
              color="critical"
              value={followUpCounts.overdue}
              label="Follow-Ups Overdue"
              onClick={() => onNavigate('followup')}
            />
          )}
        </div>
      )}

      <div className="overview-columns">
        <div className="chart-card">
          <div className="trend-chart-header">
            <h3 className="heatmap-title">Call Volume</h3>
          </div>
          {hasKey.has('incoming') ? (
            <TrendChart points={points} />
          ) : (
            <p className="overview-empty">No permission to view call data.</p>
          )}
        </div>

        <div className="chart-card">
          <div className="trend-chart-header">
            <h3 className="heatmap-title">Needs Attention</h3>
          </div>
          {attentionItems.length === 0 ? (
            <p className="overview-empty">All caught up — nothing urgent right now.</p>
          ) : (
            <div className="overview-attention-list">
              {attentionItems.slice(0, 6).map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className="overview-attention-row"
                  onClick={() => onNavigate(item.navKey)}
                >
                  <span className={`overview-attention-dot ${item.color}`} />
                  <span className="overview-attention-text">
                    <span className="overview-attention-label">{item.label}</span>
                  </span>
                  <span className="overview-attention-count">{item.count}</span>
                  <ChevronRightIcon width={16} height={16} className="overview-attention-chevron" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="overview-columns">
        <div className="chart-card">
          <div className="trend-chart-header">
            <h3 className="heatmap-title">Recent Calls</h3>
            {hasKey.has('incoming') && (
              <button type="button" className="overview-section-link" onClick={() => onNavigate('incoming')}>
                View all
              </button>
            )}
          </div>
          {recentCalls.length === 0 ? (
            <p className="overview-empty">No recent calls.</p>
          ) : (
            <div>
              {recentCalls.map((call) => {
                const status = getCallStatus(call);
                return (
                  <div key={call.id} className="overview-recent-row">
                    <div className="overview-recent-main">
                      <span className="overview-recent-number">{getCustomerNumber(call)}</span>
                      <span className="overview-recent-meta">{call.answered_agent_name || 'Unassigned'}</span>
                    </div>
                    <span className={`status ${status.toLowerCase()}`}>{status}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="chart-card">
          <div className="trend-chart-header">
            <h3 className="heatmap-title">Quick Actions</h3>
          </div>
          {toolItems.length === 0 ? (
            <p className="overview-empty">Nothing to show.</p>
          ) : (
            <div className="overview-quick-actions">
              {toolItems.map((item) => (
                <button key={item.key} type="button" className="overview-quick-action" onClick={() => onNavigate(item.key)}>
                  <item.icon width={16} height={16} />
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <Section title="Call Activity" visible={hasKey.has('ivrpresses') || hasKey.has('agents') || hasKey.has('incoming')}>
        <div className="overview-kpi-grid">
          {hasKey.has('incoming') && (
            <>
              <KpiCard icon={CloseIcon} color="danger" value={callTotals.missed} label="Missed" onClick={() => onNavigate('incoming')} />
              <KpiCard icon={RefreshIcon} color="warning" value={callTotals.attempted} label="Attempted" onClick={() => onNavigate('incoming')} />
            </>
          )}
          {hasKey.has('ivrpresses') && (
            <KpiCard icon={HashIcon} color="primary" value={pressedInRange} label="IVR Presses" onClick={() => onNavigate('ivrpresses')} />
          )}
          {hasKey.has('agents') && (
            <KpiCard icon={UsersIcon} color="primary" value={agentCount} label="Agents Tracked" onClick={() => onNavigate('agents')} />
          )}
        </div>
      </Section>

      <Section title="Customer Follow-Up" visible={hasKey.has('followup') || hasKey.has('orderqueries')}>
        <div className="overview-kpi-grid">
          {hasKey.has('followup') &&
            (sections.followup?.status === 'error' ? (
              <SectionError message={sections.followup.error} />
            ) : (
              <KpiCard icon={CalendarClockIcon} color="warning" value={followUpCounts.pending} label="Pending Follow-Ups" onClick={() => onNavigate('followup')} />
            ))}
          {hasKey.has('orderqueries') &&
            (sections.orderqueries?.status === 'error' ? (
              <SectionError message={sections.orderqueries.error} />
            ) : (
              <>
                <KpiCard icon={FlagIcon} color="warning" value={sections.orderqueries?.data?.pending} label="Pending Queries" onClick={() => onNavigate('orderqueries')} />
                <KpiCard icon={FlagIcon} color="primary" value={sections.orderqueries?.data?.open} label="Open Queries" onClick={() => onNavigate('orderqueries')} />
                <KpiCard icon={FlagIcon} color="success" value={sections.orderqueries?.data?.done} label="Solved Queries" onClick={() => onNavigate('orderqueries')} />
              </>
            ))}
        </div>
      </Section>

      <Section
        title="Orders & Payments"
        visible={
          hasKey.has('dailyorders') ||
          hasKey.has('abandonedcarts') ||
          hasKey.has('international') ||
          hasKey.has('paymentlinks') ||
          hasKey.has('backendpayments')
        }
      >
        <div className="overview-kpi-grid">
          {hasKey.has('dailyorders') &&
            (sections.dailyorders?.status === 'error' ? (
              <SectionError message={sections.dailyorders.error} />
            ) : (
              <>
                <KpiCard icon={CheckIcon} color="success" value={sections.dailyorders?.data?.confirmed} label="Orders Confirmed Today" onClick={() => onNavigate('dailyorders')} />
                <KpiCard icon={CloseIcon} color="danger" value={sections.dailyorders?.data?.cancelled} label="Orders Cancelled Today" onClick={() => onNavigate('dailyorders')} />
              </>
            ))}
          {hasKey.has('abandonedcarts') &&
            (sections.abandonedcarts?.status === 'error' ? (
              <SectionError message={sections.abandonedcarts.error} />
            ) : (
              <>
                <KpiCard icon={CartIcon} color="warning" value={sections.abandonedcarts?.data?.active} label="Abandoned Carts" onClick={() => onNavigate('abandonedcarts')} />
                <KpiCard icon={CartIcon} color="success" value={sections.abandonedcarts?.data?.converted} label="Converted Today" onClick={() => onNavigate('abandonedcarts')} />
              </>
            ))}
          {hasKey.has('international') &&
            (sections.international?.status === 'error' ? (
              <SectionError message={sections.international.error} />
            ) : (
              <>
                <KpiCard icon={GlobeIcon} color="warning" value={sections.international?.data?.logistics} label="Intl: Logistics Pending" onClick={() => onNavigate('international')} />
                <KpiCard icon={GlobeIcon} color="primary" value={sections.international?.data?.payment} label="Intl: Payment Pending" onClick={() => onNavigate('international')} />
                <KpiCard icon={GlobeIcon} color="primary" value={sections.international?.data?.toPack} label="Intl: Packing" onClick={() => onNavigate('international')} />
                <KpiCard icon={GlobeIcon} color="success" value={sections.international?.data?.toDispatch} label="Intl: Ready for Dispatch" onClick={() => onNavigate('international')} />
              </>
            ))}
          {hasKey.has('paymentlinks') &&
            (sections.paymentlinks?.status === 'error' ? (
              <SectionError message={sections.paymentlinks.error} />
            ) : (
              <KpiCard icon={LinkIcon} color="success" value={sections.paymentlinks?.data?.paid} label="Payment Links Paid" onClick={() => onNavigate('paymentlinks')} />
            ))}
          {hasKey.has('backendpayments') &&
            (sections.backendpayments?.status === 'error' ? (
              <SectionError message={sections.backendpayments.error} />
            ) : (
              <KpiCard icon={WalletIcon} color="primary" value={sections.backendpayments?.data?.count} label="Backend Payments Logged" onClick={() => onNavigate('backendpayments')} />
            ))}
        </div>
      </Section>

      <Section title="Messaging" visible={hasKey.has('whatsapp') || hasKey.has('email')}>
        <div className="overview-kpi-grid">
          {hasKey.has('whatsapp') &&
            (sections.whatsapp?.status === 'error' ? (
              <SectionError message={sections.whatsapp.error} />
            ) : (
              <>
                <KpiCard icon={WhatsAppIcon} color="success" value={sections.whatsapp?.data?.total} label="WhatsApp Conversations" onClick={() => onNavigate('whatsapp')} />
                <KpiCard icon={WhatsAppIcon} color="warning" value={sections.whatsapp?.data?.unread} label="Unread WhatsApp" onClick={() => onNavigate('whatsapp')} />
              </>
            ))}
          {hasKey.has('email') &&
            (sections.email?.status === 'error' ? (
              <SectionError message={sections.email.error} />
            ) : (
              <>
                <KpiCard icon={MailIcon} color="primary" value={sections.email?.data?.total} label="Email Conversations" onClick={() => onNavigate('email')} />
                <KpiCard icon={MailIcon} color="warning" value={sections.email?.data?.unread} label="Unread Email" onClick={() => onNavigate('email')} />
              </>
            ))}
        </div>
      </Section>

      {isSuperadmin && hasKey.has('admins') && (
        <Section title="Administration" visible>
          <div className="overview-kpi-grid">
            {sections.admins?.status === 'error' ? (
              <SectionError message={sections.admins.error} />
            ) : (
              <>
                <KpiCard icon={ShieldIcon} color="critical" value={sections.admins?.data?.admins} label="Admins" onClick={() => onNavigate('admins')} />
                <KpiCard icon={ShieldIcon} color="primary" value={sections.admins?.data?.roles} label="Roles" onClick={() => onNavigate('admins')} />
              </>
            )}
          </div>
        </Section>
      )}
    </div>
  );
}

export default Overview;
